import axios from 'axios';
import Toast from '../../utils/Toast';
import { ONE_DRIVE_AZURE_CLIENT_ID } from '../../../../constants/Constants';
import { logInfo } from '../../../../services/flowLogs';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';

export const openAuthenticationEmailPicker = async ({ instance }) => {
  if (!window.OneDrive) {
    console.error('OneDrive SDK is not loaded');
    return null;
  }

  try {
    // Define the scopes
    const scopes = [
      'User.Read',
      'Files.Read',
      'Files.Read.All',
      'Files.Read.Selected',
      'offline_access',
    ];

    // Attempt to get an access token silently
    const silentRequest = {
      scopes,
    };

    let authResponse;
    try {
      // Try to acquire token silently
      authResponse = await instance.acquireTokenSilent(silentRequest);
    } catch (silentError) {
      console.warn(
        'Silent token acquisition failed, fallback to interactive login:',
        silentError,
      );

      // Silent acquisition failed, fallback to interactive login
      authResponse = await instance.loginPopup({
        scopes,
        prompt: 'select_account',
      });
    }
    const { accessToken } = authResponse;
    const accountEmailId = authResponse?.account?.username;

    return { accountEmailId, accessTokenFromMicrosoft: accessToken };
  } catch (error) {
    console.error('Authentication failed:', error);
  }
};

export const openPathSelectionPicker = async () =>
  new Promise((resolve, reject) => {
    const odOptions = {
      clientId: ONE_DRIVE_AZURE_CLIENT_ID,
      action: 'query',
      multiSelect: false,
      viewType: 'folders',
      advanced: {
        queryParameters: 'scope=Files.Read Files.Read.All Files.Read.Selected',
      },
      success: (files) => {
        if (files.value.length > 0) {
          const folder = files.value[0];
          const folderId = folder.id;
          const { driveId } = folder.parentReference;
          resolve({
            odFolderId: folderId,
            odDriveId: driveId,
            response: files.value,
          });
        } else {
          reject(new Error('Folder not selected'));
        }
      },
      error: (e) => {
        Toast({
          message: 'Error during OneDrive Picker operation',
          variant: 'error',
        });
        reject(new Error(e));
      },
    };
    window.OneDrive.open(odOptions);
  });

export const fetchFolderDetails = async (
  odDriveId,
  odFolderId,
  accessToken,
) => {
  try {
    const response = await axios.get(
      `https://graph.microsoft.com/v1.0/drives/${odDriveId}/items/${odFolderId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response?.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      Toast({
        message: `${error.response?.data?.error?.message}`,
        variant: 'error',
      });
    } else {
      Toast({
        message: 'Error fetching OneDrive folder details',
        variant: 'error',
      });
    }
  }
};

export const fetchFiles = async (
  odDriveId,
  odFolderId,
  accessToken,
  updateAtTime,
  flowId,
  instance,
) => {
  try {
    let validAccessToken = accessToken;
    try {
      const tokenRequest = {
        scopes: ['Files.Read', 'Files.Read.All', 'offline_access'],
      };
      const tokenCheckResponse =
        await instance.acquireTokenSilent(tokenRequest);
      validAccessToken = tokenCheckResponse.accessToken; // Update with the new valid token if needed
    } catch (tokenError) {
      // If token acquisition fails silently, fallback to login
      console.warn(
        `Access token is invalid or expired. Trying to acquire a new token. ${tokenError?.message}`,
      );
      try {
        const loginResponse = await instance.loginPopup({
          scopes: ['Files.Read', 'Files.Read.All', 'offline_access'],
        });
        validAccessToken = loginResponse.accessToken;
      } catch (loginError) {
        throw new Error(
          `Unable to acquire a new access token ${loginError?.message}`,
        );
      }
    }

    const response = await axios.get(
      `https://graph.microsoft.com/v1.0/drives/${odDriveId}/items/${odFolderId}/children`,
      {
        headers: { Authorization: `Bearer ${validAccessToken}` },
      },
    );

    const timeCalcFromUpdateTime = new Date(updateAtTime).getTime();
    return response?.data?.value?.filter((file) => {
      const fileTime = new Date(file.createdDateTime).getTime();
      return fileTime >= timeCalcFromUpdateTime;
    });
  } catch (error) {
    logInfo(
      {
        data: `Error During OneDrive Folder Path Files Not Fetched : ${error?.message}`,
        label: 'error',
      },
      flowId,
    );
    Toast({
      message: 'Error During OneDrive Folder Path Files Not Fetched',
      variant: 'error',
    });
  }
};

export const handleFiles = (files, accessToken, flowId) => {
  const allowedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ];
  const maxSizeInBytes = 10 * 1024 * 1024; // max size allowed 10mb

  const filesFormatNotValid = files.filter(
    (file) => !allowedFileTypes.includes(file?.file?.mimeType),
  );

  filesFormatNotValid.forEach((file) => {
    logInfo(
      {
        data: `${file.name} ${DOCUMENT_TOAST.FORMAT_INVALID}`,
        label: 'error',
      },
      flowId,
    );
    Toast({
      message: `${file.name} ${DOCUMENT_TOAST.FORMAT_INVALID}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const filesFormatValid = files.filter((file) =>
    allowedFileTypes.includes(file?.file?.mimeType),
  );

  const filesSizeTooLarge = filesFormatValid?.filter(
    (file) => file.size > maxSizeInBytes,
  );
  filesSizeTooLarge?.forEach((file) => {
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.FILE_SIZE_TOO_LARGE}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const sizeLimitFilesValid = filesFormatValid?.filter(
    (file) => file.size <= maxSizeInBytes,
  );

  const fileNamesWithSpacesOrSpecialChars = sizeLimitFilesValid.filter((file) =>
    /[ !@#$%^&*]/.test(file.name),
  );

  fileNamesWithSpacesOrSpecialChars.forEach((file) => {
    logInfo(
      {
        data: `"${file.name}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
        label: 'error',
      },
      flowId,
    );
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const fileNamesWithoutSpacesOrSpecialChars = sizeLimitFilesValid.filter(
    (file) => /^[^\s!@#$%^&*]+$/.test(file.name),
  );
  const filesWithDownloadUrls = fileNamesWithoutSpacesOrSpecialChars.map(
    (file) => ({
      name: file.name,
      mimeType: file.file.mimeType,
      downloadUrl: file['@microsoft.graph.downloadUrl'],
    }),
  );

  return {
    files: filesWithDownloadUrls,
    accessToken,
  };
};
